/**
 * Pure client-side filter helpers for Best Odds / Market Anomalies.
 * No React, no localStorage — safe to unit-test and reuse from context/hooks.
 *
 * Filter semantics:
 * - minSpread: keep items with spread >= value; null/undefined/0 = no spread filter
 * - maxOdds: keep items with bestOdds <= value; null/undefined/0 = no max-odds filter
 * - searchQuery: case-insensitive match on teamOne, teamTwo, championship; blank = no search
 * - selectedBookmakers: null/undefined = all; array = bestBookmaker must be in list (empty => none)
 * - selectedChampionships: null/undefined = all; array = event.championship must be in list
 * - selectedMarketKeys: null/undefined = all; array = marketKey must be in list
 * - timeWindow: 'all' | 'today' | 'next24h' | 'next48h' (client local timezone)
 */

/** Spread chip thresholds used by the radar UI (0 = "All"). */
export const SPREAD_CHIP_VALUES = [0, 7, 10, 15];

/** Max-odds chip thresholds used by the radar UI (0 = "All"). */
export const MAX_ODDS_CHIP_VALUES = [0, 5, 7, 10];

/** Default filter state (no restrictions). */
export const DEFAULT_BEST_ODDS_FILTERS = Object.freeze({
  minSpread: 0,
  maxOdds: 0,
  searchQuery: '',
  selectedBookmakers: null,
  selectedChampionships: null,
  selectedMarketKeys: null,
  timeWindow: 'all'
});

/** Allowed timeWindow preset ids. */
export const TIME_WINDOW_VALUES = Object.freeze(['all', 'today', 'next24h', 'next48h']);

/** Milliseconds in one hour (local wall-clock window helpers). */
const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * Format a Date as YYYY-MM-DD in the client's local timezone.
 * @param {Date} date
 * @returns {string}
 */
export const formatLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse event.date + event.time as a kickoff Date in the CLIENT LOCAL timezone.
 * date: "YYYY-MM-DD"; time: "HH:MM" or "HH:MM:SS" (missing time → 00:00 local).
 * @param {Object} event
 * @returns {Date|null}
 */
export const parseEventKickoffLocal = (event) => {
  const dateStr = event?.date;
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) {
    return null;
  }

  const [year, month, day] = String(dateStr).split('-').map(Number);
  const timeStr = event?.time ? String(event.time).trim() : '00:00';
  const timeMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(timeStr);
  const hours = timeMatch ? Number(timeMatch[1]) : 0;
  const minutes = timeMatch ? Number(timeMatch[2]) : 0;
  const seconds = timeMatch && timeMatch[3] ? Number(timeMatch[3]) : 0;

  if (
    !Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)
    || !Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)
  ) {
    return null;
  }

  const kickoff = new Date(year, month - 1, day, hours, minutes, seconds);
  return Number.isNaN(kickoff.getTime()) ? null : kickoff;
};

/**
 * Normalize a search string for comparison.
 * @param {string|null|undefined} value
 * @returns {string}
 */
export const normalizeSearchText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim().toLowerCase();
};

/**
 * Keep anomalies whose spread is >= minSpread.
 * @param {Array} anomalies
 * @param {number|null|undefined} minSpread
 * @returns {Array}
 */
export const applyMinSpread = (anomalies, minSpread) => {
  if (!Array.isArray(anomalies)) {
    return [];
  }

  const threshold = Number(minSpread);
  if (!Number.isFinite(threshold) || threshold <= 0) {
    return anomalies;
  }

  return anomalies.filter((item) => Number(item?.spread) >= threshold);
};

/**
 * Keep anomalies whose bestOdds is <= maxOdds.
 * @param {Array} anomalies
 * @param {number|null|undefined} maxOdds
 * @returns {Array}
 */
export const applyMaxOdds = (anomalies, maxOdds) => {
  if (!Array.isArray(anomalies)) {
    return [];
  }

  const threshold = Number(maxOdds);
  if (!Number.isFinite(threshold) || threshold <= 0) {
    return anomalies;
  }

  return anomalies.filter((item) => Number(item?.bestOdds) <= threshold);
};

/**
 * Keep anomalies matching searchQuery on teams or championship.
 * @param {Array} anomalies
 * @param {string|null|undefined} searchQuery
 * @returns {Array}
 */
export const applySearch = (anomalies, searchQuery) => {
  if (!Array.isArray(anomalies)) {
    return [];
  }

  const query = normalizeSearchText(searchQuery);
  if (!query) {
    return anomalies;
  }

  return anomalies.filter((item) => {
    const event = item?.event || {};
    const haystack = [
      event.teamOne,
      event.teamTwo,
      event.championship
    ]
      .map(normalizeSearchText)
      .join(' ');

    return haystack.includes(query);
  });
};

/**
 * Keep anomalies whose bestBookmaker is in the selected list.
 * null/undefined selectedBookmakers = no bookmaker filter.
 * @param {Array} anomalies
 * @param {string[]|null|undefined} selectedBookmakers
 * @returns {Array}
 */
export const applyBookmakers = (anomalies, selectedBookmakers) => {
  if (!Array.isArray(anomalies)) {
    return [];
  }

  if (selectedBookmakers === null || selectedBookmakers === undefined) {
    return anomalies;
  }

  if (!Array.isArray(selectedBookmakers)) {
    return anomalies;
  }

  if (selectedBookmakers.length === 0) {
    return [];
  }

  const allowed = new Set(
    selectedBookmakers
      .filter((id) => id !== null && id !== undefined && String(id).length > 0)
      .map((id) => String(id))
  );

  if (allowed.size === 0) {
    return [];
  }

  return anomalies.filter((item) => allowed.has(String(item?.bestBookmaker || '')));
};

/**
 * Keep anomalies whose event.championship is in the selected list.
 * null/undefined selectedChampionships = no championship filter.
 * @param {Array} anomalies
 * @param {string[]|null|undefined} selectedChampionships
 * @returns {Array}
 */
export const applyChampionships = (anomalies, selectedChampionships) => {
  if (!Array.isArray(anomalies)) {
    return [];
  }

  if (selectedChampionships === null || selectedChampionships === undefined) {
    return anomalies;
  }

  if (!Array.isArray(selectedChampionships)) {
    return anomalies;
  }

  if (selectedChampionships.length === 0) {
    return [];
  }

  const allowed = new Set(
    selectedChampionships
      .filter((name) => name !== null && name !== undefined && String(name).trim().length > 0)
      .map((name) => String(name))
  );

  if (allowed.size === 0) {
    return [];
  }

  return anomalies.filter((item) => allowed.has(String(item?.event?.championship || '')));
};

/**
 * Keep anomalies whose marketKey is in the selected list.
 * null/undefined selectedMarketKeys = no market filter.
 * @param {Array} anomalies
 * @param {string[]|null|undefined} selectedMarketKeys
 * @returns {Array}
 */
export const applyMarketKeys = (anomalies, selectedMarketKeys) => {
  if (!Array.isArray(anomalies)) {
    return [];
  }

  if (selectedMarketKeys === null || selectedMarketKeys === undefined) {
    return anomalies;
  }

  if (!Array.isArray(selectedMarketKeys)) {
    return anomalies;
  }

  if (selectedMarketKeys.length === 0) {
    return [];
  }

  const allowed = new Set(
    selectedMarketKeys
      .filter((key) => key !== null && key !== undefined && String(key).trim().length > 0)
      .map((key) => String(key))
  );

  if (allowed.size === 0) {
    return [];
  }

  return anomalies.filter((item) => allowed.has(String(item?.marketKey || '')));
};

/**
 * Keep anomalies whose kickoff falls in the selected time window.
 *
 * Timezone: CLIENT LOCAL. event.date/time are interpreted as local wall-clock
 * (not UTC). Pass `now` for deterministic tests.
 *
 * @param {Array} anomalies
 * @param {string|null|undefined} timeWindow - all | today | next24h | next48h
 * @param {Date} [now]
 * @returns {Array}
 */
export const applyTimeWindow = (anomalies, timeWindow, now = new Date()) => {
  if (!Array.isArray(anomalies)) {
    return [];
  }

  const windowId = TIME_WINDOW_VALUES.includes(timeWindow)
    ? timeWindow
    : DEFAULT_BEST_ODDS_FILTERS.timeWindow;

  if (windowId === 'all') {
    return anomalies;
  }

  if (windowId === 'today') {
    const todayKey = formatLocalDateKey(now);
    return anomalies.filter((item) => String(item?.event?.date || '') === todayKey);
  }

  if (windowId === 'next24h' || windowId === 'next48h') {
    const hours = windowId === 'next48h' ? 48 : 24;
    const end = new Date(now.getTime() + hours * MS_PER_HOUR);
    return anomalies.filter((item) => {
      const kickoff = parseEventKickoffLocal(item?.event);
      if (!kickoff) {
        return false;
      }
      return kickoff >= now && kickoff <= end;
    });
  }

  return anomalies;
};

/**
 * Apply active filters.
 * Order: search → minSpread → maxOdds → bookmakers → championships → markets → timeWindow.
 *
 * @param {Array} anomalies
 * @param {Object} [filters]
 * @param {Date} [now] - optional clock for timeWindow (tests)
 * @returns {Array}
 */
export const filterAnomalies = (anomalies, filters = {}, now = new Date()) => {
  if (!Array.isArray(anomalies)) {
    return [];
  }

  const {
    minSpread = DEFAULT_BEST_ODDS_FILTERS.minSpread,
    maxOdds = DEFAULT_BEST_ODDS_FILTERS.maxOdds,
    searchQuery = DEFAULT_BEST_ODDS_FILTERS.searchQuery,
    selectedBookmakers = DEFAULT_BEST_ODDS_FILTERS.selectedBookmakers,
    selectedChampionships = DEFAULT_BEST_ODDS_FILTERS.selectedChampionships,
    selectedMarketKeys = DEFAULT_BEST_ODDS_FILTERS.selectedMarketKeys,
    timeWindow = DEFAULT_BEST_ODDS_FILTERS.timeWindow
  } = filters || {};

  let result = anomalies;
  result = applySearch(result, searchQuery);
  result = applyMinSpread(result, minSpread);
  result = applyMaxOdds(result, maxOdds);
  result = applyBookmakers(result, selectedBookmakers);
  result = applyChampionships(result, selectedChampionships);
  result = applyMarketKeys(result, selectedMarketKeys);
  result = applyTimeWindow(result, timeWindow, now);
  return result;
};

/**
 * Whether any applied filter currently restricts the list.
 * @param {Object} [filters]
 * @returns {boolean}
 */
export const hasActiveBestOddsFilters = (filters = {}) => {
  const {
    minSpread = 0,
    maxOdds = 0,
    searchQuery = '',
    selectedBookmakers = null,
    selectedChampionships = null,
    selectedMarketKeys = null,
    timeWindow = 'all'
  } = filters || {};

  if (Number(minSpread) > 0) {
    return true;
  }

  if (Number(maxOdds) > 0) {
    return true;
  }

  if (normalizeSearchText(searchQuery)) {
    return true;
  }

  if (Array.isArray(selectedBookmakers)) {
    return true;
  }

  if (Array.isArray(selectedChampionships)) {
    return true;
  }

  if (Array.isArray(selectedMarketKeys)) {
    return true;
  }

  if (timeWindow && timeWindow !== 'all' && TIME_WINDOW_VALUES.includes(timeWindow)) {
    return true;
  }

  return false;
};

export default {
  SPREAD_CHIP_VALUES,
  MAX_ODDS_CHIP_VALUES,
  DEFAULT_BEST_ODDS_FILTERS,
  TIME_WINDOW_VALUES,
  formatLocalDateKey,
  parseEventKickoffLocal,
  normalizeSearchText,
  applyMinSpread,
  applyMaxOdds,
  applySearch,
  applyBookmakers,
  applyChampionships,
  applyMarketKeys,
  applyTimeWindow,
  filterAnomalies,
  hasActiveBestOddsFilters
};
